/**
 * Untitled UI Side Panel Component
 *
 * A slide-out panel following Untitled UI design patterns.
 * Built with React Aria for full accessibility support.
 *
 * @example
 * // Basic usage
 * <SidePanel isOpen={isOpen} onClose={handleClose}>
 *   <SidePanelHeader title="Panel Title" onClose={handleClose} />
 *   <SidePanelBody>Content goes here</SidePanelBody>
 *   <SidePanelFooter>
 *     <Button>Cancel</Button>
 *     <Button color="primary">Save</Button>
 *   </SidePanelFooter>
 * </SidePanel>
 *
 * @example
 * // Filter panel variant
 * <SidePanel isOpen={isOpen} onClose={handleClose} variant="filter" size="md">
 *   <SidePanelHeader title="Filters" subtitle="Refine your results" onClose={handleClose} />
 *   <SidePanelBody>Filter controls</SidePanelBody>
 *   <SidePanelFooter align="between">
 *     <Button color="tertiary">Reset</Button>
 *     <Button color="primary">Apply filters</Button>
 *   </SidePanelFooter>
 * </SidePanel>
 */

import {
  Dialog,
  Modal as AriaModal,
  ModalOverlay,
  Heading,
  type DialogProps,
} from 'react-aria-components'
import { XCloseIcon } from '@untitledui-icons/react/line'
import { cn } from '@/lib/utils'
import { Button } from '@/components/base/buttons'
import type {
  SidePanelProps,
  SidePanelHeaderProps,
  SidePanelBodyProps,
  SidePanelFooterProps,
  SidePanelSize,
  SidePanelSide,
  SidePanelVariant,
} from './side-panel-types'

// ============================================================================
// Size & Variant Configurations
// ============================================================================

const sizeConfig: Record<SidePanelSize, string> = {
  sm: 'max-w-sm', // 384px
  md: 'max-w-md', // 448px
  lg: 'max-w-lg', // 512px
  xl: 'max-w-xl', // 576px
  '2xl': 'max-w-2xl', // 672px
  full: 'max-w-4xl', // 896px
}

const variantConfig: Record<SidePanelVariant, { container: string; header: string }> = {
  default: {
    container: '',
    header: '',
  },
  prominent: {
    container: '',
    header: 'bg-gray-50 dark:bg-gray-800/50',
  },
  form: {
    container: '',
    header: 'border-b border-gray-200 dark:border-gray-700',
  },
  details: {
    container: '',
    header: 'bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700',
  },
  filter: {
    container: '',
    header: '',
  },
  notification: {
    container: '',
    header: 'pb-4',
  },
}

const slideAnimations: Record<SidePanelSide, { enter: string; exit: string }> = {
  right: {
    enter: 'entering:animate-in entering:slide-in-from-right entering:duration-300',
    exit: 'exiting:animate-out exiting:slide-out-to-right exiting:duration-200',
  },
  left: {
    enter: 'entering:animate-in entering:slide-in-from-left entering:duration-300',
    exit: 'exiting:animate-out exiting:slide-out-to-left exiting:duration-200',
  },
}

// ============================================================================
// SidePanel Root Component
// ============================================================================

export function SidePanel({
  isOpen,
  onOpenChange,
  onClose,
  side = 'right',
  size = 'md',
  variant = 'default',
  isDismissable = true,
  isKeyboardDismissDisabled = false,
  showOverlay = true,
  className,
  overlayClassName,
  children,
}: SidePanelProps) {
  const handleOpenChange = (open: boolean) => {
    onOpenChange?.(open)
    if (!open) {
      onClose?.()
    }
  }

  const animations = slideAnimations[side]
  const variantStyles = variantConfig[variant]

  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      isDismissable={isDismissable}
      isKeyboardDismissDisabled={isKeyboardDismissDisabled}
      className={cn(
        'fixed inset-0 z-50',
        showOverlay && 'bg-black/50',
        'entering:animate-in entering:fade-in-0 entering:duration-200',
        'exiting:animate-out exiting:fade-out-0 exiting:duration-200',
        overlayClassName
      )}
    >
      <AriaModal
        className={cn(
          'fixed inset-y-0 h-full w-full',
          'bg-white shadow-xl dark:bg-gray-900',
          'flex flex-col',
          side === 'right' && 'right-0',
          side === 'left' && 'left-0',
          sizeConfig[size],
          animations.enter,
          animations.exit,
          variantStyles.container,
          className
        )}
      >
        {children}
      </AriaModal>
    </ModalOverlay>
  )
}

// ============================================================================
// SidePanelContent Component
// ============================================================================

export interface SidePanelContentProps extends DialogProps {
  children: React.ReactNode
}

export function SidePanelContent({ children, className, ...props }: SidePanelContentProps) {
  return (
    <Dialog
      className={cn('flex h-full flex-col outline-none', className)}
      {...props}
    >
      {children}
    </Dialog>
  )
}

// ============================================================================
// SidePanelHeader Component
// ============================================================================

export function SidePanelHeader({
  title,
  subtitle,
  icon,
  featuredIcon,
  steps,
  currentStep,
  onClose,
  showDivider = true,
  className,
  children,
}: SidePanelHeaderProps) {
  const hasSteps = steps && steps.length > 0

  return (
    <div
      className={cn(
        'shrink-0 px-6 py-4',
        showDivider && 'border-b border-gray-200 dark:border-gray-700',
        className
      )}
    >
      {/* Featured icon */}
      {featuredIcon && (
        <div className="mb-4 flex items-center justify-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-100 dark:bg-brand-900/30">
            {featuredIcon}
          </div>
        </div>
      )}

      {/* Header content row */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          {/* Icon */}
          {icon && (
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
              {icon}
            </div>
          )}

          {/* Title and subtitle */}
          <div className="flex flex-col">
            {title && (
              <Heading slot="title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {title}
              </Heading>
            )}
            {subtitle && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {subtitle}
              </p>
            )}
          </div>
        </div>

        {/* Close button */}
        {onClose && (
          <Button
            color="tertiary"
            size="sm"
            onPress={onClose}
            className="shrink-0 p-2 hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Close panel"
            iconLeading={XCloseIcon}
          />
        )}
      </div>

      {/* Step indicator */}
      {hasSteps && (
        <div className="mt-4 flex items-center gap-2">
          {steps.map((step, index) => (
            <div key={index} className="flex items-center gap-2">
              <div
                className={cn(
                  'flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium',
                  index === currentStep
                    ? 'bg-brand-500 text-white'
                    : step.isComplete
                      ? 'bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-400'
                      : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                )}
              >
                {index + 1}
              </div>
              <span
                className={cn(
                  'text-sm',
                  index === currentStep
                    ? 'font-medium text-gray-900 dark:text-gray-100'
                    : 'text-gray-500 dark:text-gray-400'
                )}
              >
                {step.title}
              </span>
              {index < steps.length - 1 && (
                <div className="mx-2 h-px w-8 bg-gray-200 dark:bg-gray-700" />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Custom children content */}
      {children}
    </div>
  )
}

// ============================================================================
// SidePanelBody Component
// ============================================================================

export function SidePanelBody({ noPadding = false, className, children }: SidePanelBodyProps) {
  return (
    <div
      className={cn(
        'flex-1 overflow-y-auto',
        !noPadding && 'px-6 py-4',
        className
      )}
    >
      {children}
    </div>
  )
}

// ============================================================================
// SidePanelFooter Component
// ============================================================================

const footerAlignConfig = {
  left: 'justify-start',
  right: 'justify-end',
  between: 'justify-between',
  center: 'justify-center',
}

export function SidePanelFooter({
  align = 'right',
  showDivider = true,
  className,
  children,
}: SidePanelFooterProps) {
  return (
    <div
      className={cn(
        'flex shrink-0 items-center gap-3 px-6 py-4',
        showDivider && 'border-t border-gray-200 dark:border-gray-700',
        footerAlignConfig[align],
        className
      )}
    >
      {children}
    </div>
  )
}

// ============================================================================
// SidePanelSection Component (for grouping content)
// ============================================================================

export interface SidePanelSectionProps {
  /** Section title */
  title?: string
  /** Section description */
  description?: string
  /** Additional className */
  className?: string
  /** Section content */
  children: React.ReactNode
}

export function SidePanelSection({ title, description, className, children }: SidePanelSectionProps) {
  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {(title || description) && (
        <div className="flex flex-col gap-1">
          {title && (
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {title}
            </h3>
          )}
          {description && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {description}
            </p>
          )}
        </div>
      )}
      {children}
    </div>
  )
}

// ============================================================================
// Exports
// ============================================================================

export type {
  SidePanelProps,
  SidePanelHeaderProps,
  SidePanelBodyProps,
  SidePanelFooterProps,
  SidePanelSize,
  SidePanelSide,
  SidePanelVariant,
  SidePanelStep,
} from './side-panel-types'

export default SidePanel
